/**
 * Jobs Page
 * Find work: My Bookings, Gigs, and My Bids with job cards and CTAs
 * iOS-first design with consistent styling (WalletPage, MembershipPage)
 *
 * @author Varun Pandey
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Briefcase,
  MapPin,
  Star,
  IndianRupee,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Sheet } from '../../components/ui/Sheet';
import { Loader } from '@/components/ui';
import { ApplyBidDrawer } from '../../components/features/ApplyBidDrawer';
import { cn } from '../../utils/cn';
import { subtractMinutes, toAmPm } from '@/utils/time';
import apiClient from '../../api/client';
import { API_ENDPOINTS } from '../../api/endpoints';
import { useArtistDetailsStore } from '../../stores/artistDetailsStore';
import { useEventPricingStore } from '../../stores/eventPricingStore';
import { useJobsGigsStore } from '../../stores/jobsGigsStore';
import type { JobsTypeFilter } from '../../stores/jobsGigsStore';
import { useJobsLikedStore } from '../../stores/jobsLikedStore';
import { useJobsBidsStore } from '../../stores/jobsBidsStore';
import { useBookingFormsStore } from '../../stores/bookingFormsStore';
import type { EventPricingApiItem } from '../../services/artistService';
import { EVENT_TYPES } from '../../constants/eventTypes';
import { MyBookingsTabContent } from './jobs/MyBookingsTabContent';
import { GigsTabContent } from './jobs/GigsTabContent';
import { SavedJobsTabContent } from './jobs/SavedJobsTabContent';
import { MyBidsTabContent } from './jobs/MyBidsTabContent';
import { isSingerOrLiveBandCategory } from '../../data/membershipPlansData';
import { defaultGigsTypeForCategory, getGigsTypeTabs } from './jobs/gigsTypeConfig';
import { buildExpressEventCandidates, getActiveExpressEvent } from '@/services/eventOngoingService';
import { toEventTypeDisplayLabel } from '@/utils/eventTypeDisplay';

type JobTab = 'express' | 'gigs' | 'bids' | 'saved';

/** Job list item from api/v2/jobs/ */
export interface JobsApiJob {
  id: number;
  title: string;
  tbl: string;
  date: string;
  location: string;
  description: string;
  duration: number;
  audience: number;
  event_type: string;
  budget: string;
  is_applied: boolean;
  is_liked: boolean;
  pitched_artists: number;
  price: { original: number; discount: number; updated_price: number };
  status: boolean;
  type?: 'Basic' | 'Recommended' | 'Express';
  can_claim?: boolean;
  blocked_reason?: string | null;
  distance_km?: number;
  venue_type?: string | null;
}

export interface JobsApiResponse {
  success: boolean;
  data: {
    results: JobsApiJob[];
    recommended: unknown[];
    total_liked_jobs: number;
    total_applied_jobs?: number;
    count: number;
    next: string | null;
    previous: string | null;
  };
}

interface JobDetailApiData {
  deal_id: number;
  id?: number;
  title?: string;
  date?: string;
  location?: string;
  description?: string;
  duration?: number;
  audience?: number;
  event_type?: string;
  budget?: string;
}

interface JobDetailApiResponse {
  success: boolean;
  data: JobDetailApiData;
}

function mapJobsApiToCardData(job: JobsApiJob): JobCardData {
  const badge =
    job.type === 'Recommended'
      ? ('AI Recommended' as const)
      : job.type === 'Express'
        ? ('Express' as const)
        : undefined;
  return {
    id: String(job.id),
    category: job.event_type,
    amount: 0,
    budgetDisplay: job.budget,
    eventType: job.event_type,
    city: '',
    date: job.date,
    location: job.location,
    durationMinutes: job.duration,
    attendees: job.audience,
    eventTag: job.event_type,
    coinsToApply: job.price.updated_price,
    isSaved: job.is_liked,
    badge,
    title: job.title,
    description: job.description,
    type: job.type,
    canClaim: job.can_claim,
    blockedReason: job.blocked_reason ?? null,
    distance_km: job.distance_km,
    venue_type: job.venue_type ?? null,
  };
}

function parseJobDateToTs(dateStr: string | null | undefined): number | null {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const raw = dateStr.trim();
  if (!raw) return null;

  const direct = new Date(raw);
  if (!Number.isNaN(direct.getTime())) return direct.getTime();

  // Example seen in UI: "10 May, 2026" or "10 May 2026"
  const m = raw.match(/^(\d{1,2})\s+([A-Za-z]+),?\s+(\d{4})$/);
  if (m) {
    const day = Number(m[1]);
    const monLabel = m[2].slice(0, 3).toLowerCase();
    const year = Number(m[3]);
    const monthIdx: Record<string, number> = {
      jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
      jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
    };
    const mi = monthIdx[monLabel];
    if (Number.isFinite(day) && Number.isFinite(year) && Number.isFinite(mi)) {
      const d = new Date(year, mi, day, 0, 0, 0, 0);
      if (!Number.isNaN(d.getTime())) return d.getTime();
    }
  }

  return null;
}

function sortJobsByDateFromToday(jobs: JobCardData[], nowMs: number): JobCardData[] {
  const today = new Date(nowMs);
  today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();

  const withMeta = jobs.map((job, idx) => {
    const ts = parseJobDateToTs(job.date);
    return { job, idx, ts };
  });

  const upcoming: typeof withMeta = [];
  const past: typeof withMeta = [];
  const unknown: typeof withMeta = [];

  for (const item of withMeta) {
    if (item.ts == null) unknown.push(item);
    else if (item.ts >= todayMs) upcoming.push(item);
    else past.push(item);
  }

  // "from today": closest upcoming first (09 May above 10 May when today is 08 May)
  upcoming.sort((a, b) => (a.ts! - b.ts!) || (a.idx - b.idx));
  // Past jobs after upcoming; keep most recent past first
  past.sort((a, b) => (b.ts! - a.ts!) || (a.idx - b.idx));
  // Unknown dates at end
  unknown.sort((a, b) => a.idx - b.idx);

  return [...upcoming, ...past, ...unknown].map((x) => x.job);
}

/** Job list item from api/v2/jobs/bids/ */
interface JobsBidsApiJob {
  id: number;
  title: string;
  tbl: string;
  date: string;
  location: string;
  description: string;
  duration: number;
  audience: number;
  event_type: string;
  budget: string;
  is_applied: boolean;
  is_liked: boolean;
  pitched_artists: number;
  note?: string;
  bid: {
    coins_deducted: number;
    amount: number;
    applied_date?: string;
    status: string;
    negotiation_stats?: { used: number; remaining: number };
    rejection_reason?: string | null;
    can_resubmit?: boolean;
    negotiations?: unknown[];
  };
}

interface BidListCardData {
  id: string;
  title: string;
  location: string;
  budgetDisplay: string;
  coinsDeducted: number;
  status: string;
  appliedDate?: string;
  rejectionReason?: string;
  canResubmit?: boolean;
  eventType: string;
  date: string;
  durationMinutes: number;
  attendees: number;
  description: string;
}

function normalizeNullableApiText(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  if (lower === 'null' || lower === 'undefined') return '';
  if (/^bytearray\(b'.*'\)$/i.test(trimmed)) return '';
  return trimmed;
}

function mapBidsApiToBidCard(job: JobsBidsApiJob): BidListCardData {
  const normalizedLocation = normalizeNullableApiText(job.location) || 'Location unavailable';
  const normalizedEventType = normalizeNullableApiText(job.event_type) || 'Event';
  const normalizedDescription = normalizeNullableApiText(job.description);
  return {
    id: String(job.id),
    title: job.title,
    location: normalizedLocation,
    budgetDisplay: job.budget,
    coinsDeducted: Number(job.bid?.coins_deducted ?? 0),
    status: String(job.bid?.status ?? ''),
    appliedDate: typeof job.bid?.applied_date === 'string' ? job.bid.applied_date : undefined,
    rejectionReason: normalizeNullableApiText(job.bid?.rejection_reason ?? job.note),
    canResubmit: Boolean(job.bid?.can_resubmit),
    eventType: normalizedEventType,
    date: job.date,
    durationMinutes: Number(job.duration ?? 0),
    attendees: Number(job.audience ?? 0),
    description: normalizedDescription,
  };
}

import type { JobCardData } from '../../types/jobs';

/** Single booking form item from GET api/v2/artist/booking-forms/ or .../booked/ */
export interface BookingFormItem {
  id: number;
  deal_id: number;
  artist_id: number;
  artist_type: string;
  artist_act: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  full_address: string;
  city_state: string;
  members_count: number;
  members_info: string;
  report_at: string;
  performance_duration_mins: number;
  tech_rider: string;
  tbl_needed: number;
  travel_boarding_lodging: string;
  event_type: string;
  venue: string;
  venue_city_state: string;
  bank_name: string;
  branch_name: string;
  ifsc_code: string;
  account_number: string;
  account_name: string;
  account_type: string;
  pan: string;
  gstin: string | null;
  fee: string;
  advance_percentage: string;
  advance_amount: string;
  balance_amount: string;
  payment_terms: string;
  rating: number;
  feedback: string;
  status: string;
  created_at: string;
  updated_at: string;
  tech_rider_value: string;
  gst_value: string | null;
  event_date: string;
  pin_code: string | null;
  venue_city: string;
  venue_state: string;
  artist_state: string;
  artist_city: string;
  google_map_link: string | null;
  event_start_at: string | null;
  taf: string | null;
  mode: string | null;
  description: string | null;
}

/** Parsed member for form display/edit */
export interface BookingFormMember {
  name: string;
  role: string;
  age: number;
}

/** Submit payload for POST api/v2/artist/booking-forms/:id/submit/ */
export interface BookingFormSubmitPayload {
  members_info: string;
  tech_rider_value: string;
  travel_boarding_lodging: string;
  gstin: string;
  pan: string;
  bank_name: string;
  branch_name: string;
  ifsc_code: string;
  account_number: string;
  account_name: string;
  account_type: string;
  feedback: string;
  rating: number;
}

function parseMembersInfo(raw: string | null | undefined): BookingFormMember[] {
  if (!raw || typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.map((m: { name?: string; role?: string; age?: number }) => ({
      name: String(m?.name ?? ''),
      role: String(m?.role ?? ''),
      age: Number(m?.age ?? 0),
    }));
  } catch {
    return [];
  }
}

function formatBookingEventDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const day = d.getDate();
  const mon = d.toLocaleDateString('en-IN', { month: 'short' });
  const year = d.getFullYear();
  return `${day} ${mon} ${year}`;
}

function formatBookingINR(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `₹${n.toLocaleString('en-IN')}`;
}

function removeSecondsFromTime(timeStr: string | null | undefined): string {
  if (!timeStr) return '—';
  const match = timeStr.match(/^(\d{1,2}:\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (match) return `${match[1]} ${(match[2] ?? '').toUpperCase()}`.trim();
  return timeStr;
}

function formatPaymentTerms(terms: string | null | undefined): string {
  if (!terms) return '—';
  const t = String(terms).trim();
  const n = parseInt(t, 10);
  if (!Number.isNaN(n) && String(n) === t) return `Within ${n} day${n !== 1 ? 's' : ''}`;
  return t;
}

type ExpressBookingDetailData = {
  pipedrive_deal_id?: number;
  express_booking_id?: number;
  booking_id?: string;
  artist_details?: { professional_name?: string; email?: string; phone?: string };
  event_details?: {
    event_type?: string;
    event_date?: string;
    event_time?: string;
    event_duration?: number;
  };
  location?: { address?: string; city?: string; state?: string; map_link?: string };
  fee_and_payment?: {
    base_price?: string | number;
    taf_percent?: string | number;
    final_balance?: string | number;
    is_sound_setup_added?: boolean;
    payment_terms?: string;
  };
  performing_members?: { count?: number; label?: string };
};

function formatBookingEventDateLong(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

const TABS: { id: JobTab; label: string }[] = [
  { id: 'gigs', label: 'Gigs' },
  { id: 'express', label: 'My Bookings' },
  { id: 'bids', label: 'My Bids' },
  { id: 'saved', label: 'Saved Jobs' },
];

/** Normalize job event_type to slug for matching (e.g. "Video Shoutout" -> "video-shoutout") */
function eventTypeToSlug(eventType: string): string {
  return eventType
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/** Find event pricing for a job's event type. Match by event_name (case-insensitive) or e_slug. */
function getEventPricingForJob(
  eventPricingData: EventPricingApiItem[],
  jobEventType: string
): EventPricingApiItem | null {
  if (!jobEventType || !Array.isArray(eventPricingData)) return null;
  const normalized = jobEventType.trim().toLowerCase();
  const slug = eventTypeToSlug(jobEventType);
  for (const item of eventPricingData) {
    const name = (item.event?.event_name ?? '').trim().toLowerCase();
    const eslug = (item.event?.e_slug ?? '').trim().toLowerCase();
    if (name === normalized || eslug === slug || eslug === normalized) {
      return item;
    }
  }
  return null;
}

/** True if job can be applied to (event type exists in pricing with a base price). */
function canApplyToJob(
  eventPricingData: EventPricingApiItem[],
  jobEventType: string
): boolean {
  const item = getEventPricingForJob(eventPricingData, jobEventType);
  if (!item) return false;
  const base = item.base_price;
  return typeof base === 'string' && base.trim() !== '' && Number(base) > 0;
}

export const JobsPage: React.FC = () => {
  const location = useLocation();
  const { data: artistDetails } = useArtistDetailsStore();
  const [activeTab, setActiveTab] = useState<JobTab>(() => (location.state as { tab?: JobTab })?.tab === 'express' ? 'express' : 'gigs');
  const navigate = useNavigate();
  const [eventClockMs, setEventClockMs] = useState(() => Date.now());
  const [spotlightBookingDealId, setSpotlightBookingDealId] = useState<number | null>(
    () => {
      const value = Number((location.state as { spotlightBookingDealId?: number } | null)?.spotlightBookingDealId ?? 0);
      return Number.isFinite(value) && value > 0 ? value : null;
    }
  );
  const [bidDrawerOpen, setBidDrawerOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobCardData | null>(null);

  // Gigs tab: data from store, fetch once per session; refetch after like
  const [gigsPage, setGigsPage] = useState(1);
  const gigsTypeCategoryDefaultAppliedRef = useRef(false);
  const [gigsType, setGigsType] = useState<JobsTypeFilter>(() =>
    defaultGigsTypeForCategory(useArtistDetailsStore.getState().data?.artist_category?.name)
  );
  const gigsTypeTabs = useMemo(
    () => getGigsTypeTabs(artistDetails?.artist_category?.name),
    [artistDetails?.artist_category?.name]
  );
  const [gigsLikeUpdatingIds, setGigsLikeUpdatingIds] = useState<Set<string>>(new Set());
  const gigsResults = useJobsGigsStore((s) => s.results);
  const gigsCount = useJobsGigsStore((s) => s.count);
  const totalAppliedJobs = useJobsGigsStore((s) => s.totalAppliedJobs);
  const gigsLoading = useJobsGigsStore((s) => s.isLoading);
  const ensureGigsFetched = useJobsGigsStore((s) => s.ensureFetched);
  const fetchGigs = useJobsGigsStore((s) => s.fetchGigs);
  const gigsJobs = useMemo(() => {
    const mapped = gigsResults.map(mapJobsApiToCardData);
    const sorted = sortJobsByDateFromToday(mapped, eventClockMs);
    const PAGE_SIZE = 10;
    const start = (gigsPage - 1) * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [gigsResults, eventClockMs, gigsPage]);
  const jobsCount = gigsCount;

  // Saved Jobs tab: data from store, fetch once per session; refetch after like
  const [savedPage, setSavedPage] = useState(1);
  const [savedLikeUpdatingIds, setSavedLikeUpdatingIds] = useState<Set<string>>(new Set());
  const savedResults = useJobsLikedStore((s) => s.results);
  const savedJobsCount = useJobsLikedStore((s) => s.count);
  const savedLoading = useJobsLikedStore((s) => s.isLoading);
  const ensureSavedFetched = useJobsLikedStore((s) => s.ensureFetched);
  const fetchSaved = useJobsLikedStore((s) => s.fetchLiked);
  const savedJobs = useMemo(() => {
    const mapped = savedResults.map(mapJobsApiToCardData);
    return sortJobsByDateFromToday(mapped, eventClockMs);
  }, [savedResults, eventClockMs]);

  // Bids tab: data from store, fetch once per session; refetch after POST bid
  const [bidsPage, setBidsPage] = useState(1);
  const bidsResults = useJobsBidsStore((s) => s.results);
  const bidsCount = useJobsBidsStore((s) => s.count);
  const bidsLoading = useJobsBidsStore((s) => s.isLoading);
  const ensureBidsFetched = useJobsBidsStore((s) => s.ensureFetched);
  const fetchBids = useJobsBidsStore((s) => s.fetchBids);
  const bidsJobs = useMemo(() => bidsResults.map(mapBidsApiToBidCard), [bidsResults]);

  // My Bookings tab: data from store, fetch once per session; refetch after POST submit
  const bookingFormsToFill = useBookingFormsStore((s) => s.toFill);
  const bookingFormsSubmitted = useBookingFormsStore((s) => s.submitted);
  const bookingsToFillLoading = useBookingFormsStore((s) => s.toFillLoading);
  const bookingsSubmittedLoading = useBookingFormsStore((s) => s.submittedLoading);
  const fetchBookingForms = useBookingFormsStore((s) => s.fetchBookingForms);
  const activeExpressEvent = useMemo(() => {
    const candidates = buildExpressEventCandidates(bookingFormsSubmitted);
    return getActiveExpressEvent(candidates, eventClockMs);
  }, [bookingFormsSubmitted, eventClockMs]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setEventClockMs(Date.now());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (gigsTypeCategoryDefaultAppliedRef.current) return;
    const categoryName = artistDetails?.artist_category?.name;
    if (!categoryName) return;
    gigsTypeCategoryDefaultAppliedRef.current = true;
    setGigsType(defaultGigsTypeForCategory(categoryName));
  }, [artistDetails?.artist_category?.name]);

  useEffect(() => {
    const categoryName = artistDetails?.artist_category?.name;
    if (!categoryName) return;
    if (!isSingerOrLiveBandCategory(categoryName) && gigsType === 'express') {
      setGigsType('standard');
    }
  }, [artistDetails?.artist_category?.name, gigsType]);

  const eventPricingData = useEventPricingStore((s) => s.data);
  const ensureEventPricingFetched = useEventPricingStore((s) => s.ensureFetched);
  const createEventPricing = useEventPricingStore((s) => s.createEventPricing);
  const tabsSectionRef = useRef<HTMLDivElement>(null);
  const prevGigsPageRef = useRef<number | null>(null);
  const prevSavedPageRef = useRef<number | null>(null);
  const prevBidsPageRef = useRef<number | null>(null);

  // Add Event Pricing sheet (inline on Jobs page so user can add pricing without leaving)
  const [addPricingSheetOpen, setAddPricingSheetOpen] = useState(false);
  const [jobForAddPricing, setJobForAddPricing] = useState<JobCardData | null>(null);
  const [addPricingEventSlug, setAddPricingEventSlug] = useState('');
  const [addPricingBasePrice, setAddPricingBasePrice] = useState('');
  const [addPricingSubmitting, setAddPricingSubmitting] = useState(false);

  /** Offset so tabs sit below the fixed navbar (UserHome header is h-16 = 64px) */
  const NAVBAR_OFFSET_PX = 64;

  const scrollToTabs = () => {
    const el = tabsSectionRef.current;
    if (!el) return;
    // Find the scrollable parent (main content area in UserHome has overflow-y-auto)
    let container: HTMLElement | null = el.parentElement;
    while (container) {
      const { overflowY, overflow } = getComputedStyle(container);
      const scrollable = container.scrollHeight > container.clientHeight && (overflowY === 'auto' || overflowY === 'scroll' || overflow === 'auto' || overflow === 'scroll');
      if (scrollable) break;
      container = container.parentElement;
    }
    const scrollTarget = container ?? document.documentElement;
    const start = scrollTarget === document.documentElement ? window.scrollY : (scrollTarget as HTMLElement).scrollTop;
    const elTop = el.getBoundingClientRect().top;
    const containerTop = scrollTarget === document.documentElement ? 0 : (scrollTarget as HTMLElement).getBoundingClientRect().top;
    // Leave space so tabs are not hidden behind the fixed navbar
    const end = Math.max(0, start + (elTop - containerTop) - NAVBAR_OFFSET_PX);
    const distance = end - start;
    const duration = 450;
    const startTime = performance.now();
    const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
    const step = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const value = start + distance * easeOutCubic(t);
      if (scrollTarget === document.documentElement) {
        window.scrollTo(0, value);
      } else {
        (scrollTarget as HTMLElement).scrollTop = value;
      }
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  // Gigs tab: GET only when tab is active; once per session per page; refetch after like
  useEffect(() => {
    if (activeTab !== 'gigs') {
      setGigsPage(1);
      return;
    }
    void ensureGigsFetched(gigsPage, gigsType);
  }, [activeTab, gigsPage, gigsType, ensureGigsFetched]);

  // Saved Jobs tab: GET only when tab is active; once per session per page; refetch after like
  useEffect(() => {
    if (activeTab !== 'saved') {
      setSavedPage(1);
      return;
    }
    void ensureSavedFetched(savedPage);
  }, [activeTab, savedPage, ensureSavedFetched]);

  // Event pricing: fetch when Gigs or Saved Jobs tab is active (same API as Event Essentials)
  useEffect(() => {
    if (activeTab === 'gigs' || activeTab === 'saved') {
      void ensureEventPricingFetched();
    }
  }, [activeTab, ensureEventPricingFetched]);

  // Bids tab: GET only when tab is active; once per session per page; refetch after POST bid
  useEffect(() => {
    if (activeTab !== 'bids') {
      setBidsPage(1);
      return;
    }
    void ensureBidsFetched(bidsPage);
  }, [activeTab, bidsPage, ensureBidsFetched]);

  // My Bookings tab: refetch whenever tab is active so list stays fresh after claim/submit
  useEffect(() => {
    if (activeTab !== 'express') return;
    void fetchBookingForms();
  }, [activeTab, fetchBookingForms]);

  // Scroll to tabs after pagination load (gigs / saved / bids)
  useEffect(() => {
    if (gigsLoading || activeTab !== 'gigs') return;
    if (prevGigsPageRef.current !== null && prevGigsPageRef.current !== gigsPage) {
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToTabs);
      });
    }
    prevGigsPageRef.current = gigsPage;
  }, [gigsLoading, gigsPage, activeTab]);
  useEffect(() => {
    if (savedLoading || activeTab !== 'saved') return;
    if (prevSavedPageRef.current !== null && prevSavedPageRef.current !== savedPage) {
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToTabs);
      });
    }
    prevSavedPageRef.current = savedPage;
  }, [savedLoading, savedPage, activeTab]);
  useEffect(() => {
    if (bidsLoading || activeTab !== 'bids') return;
    if (prevBidsPageRef.current !== null && prevBidsPageRef.current !== bidsPage) {
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToTabs);
      });
    }
    prevBidsPageRef.current = bidsPage;
  }, [bidsLoading, bidsPage, activeTab]);

  const [bookingDrawerOpen, setBookingDrawerOpen] = useState(false);
  const [selectedBookingForm, setSelectedBookingForm] = useState<BookingFormItem | null>(null);
  const [bookingIsSubmittedView, setBookingIsSubmittedView] = useState(false);
  const [expressSubmittedDetailsLoading, setExpressSubmittedDetailsLoading] = useState(false);
  const [expressSubmittedDetails, setExpressSubmittedDetails] = useState<ExpressBookingDetailData | null>(null);
  const [bookingFormSubmitLoading, setBookingFormSubmitLoading] = useState(false);
  const [bookingFormData, setBookingFormData] = useState<{
    members: BookingFormMember[];
    techRiderValue: string;
    travelBoardingLodging: string;
    gstin: string;
    pan: string;
    bank_name: string;
    branch_name: string;
    ifsc_code: string;
    account_number: string;
    account_name: string;
    account_type: string;
    feedback: string;
    termsAccepted: boolean;
  }>({
    members: [],
    techRiderValue: '',
    travelBoardingLodging: '',
    gstin: '',
    pan: '',
    bank_name: '',
    branch_name: '',
    ifsc_code: '',
    account_number: '',
    account_name: '',
    account_type: 'Savings',
    feedback: '',
    termsAccepted: false,
  });

  useEffect(() => {
    const routeState = (location.state as { tab?: JobTab; spotlightBookingDealId?: number } | null) ?? null;
    if (routeState?.tab) setActiveTab(routeState.tab);
    const nextSpotlight = Number(routeState?.spotlightBookingDealId ?? 0);
    if (Number.isFinite(nextSpotlight) && nextSpotlight > 0) {
      setSpotlightBookingDealId(nextSpotlight);
    }
  }, [location.state]);

  const handleGigsToggleLike = async (jobId: string) => {
    if (gigsLikeUpdatingIds.has(jobId)) return;
    setGigsLikeUpdatingIds((prev) => {
      const next = new Set(prev);
      next.add(jobId);
      return next;
    });
    try {
      const url = `${API_ENDPOINTS.AUTH.JOBS}like/${jobId}/`;
      await apiClient.patch(url);
      await Promise.all([
        fetchGigs(gigsPage, gigsType),
        fetchSaved(savedPage),
      ]);
    } catch {
      toast.error('Failed to like this job. Please try again.');
    } finally {
      setGigsLikeUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  const handleSavedToggleLike = async (jobId: string) => {
    if (savedLikeUpdatingIds.has(jobId)) return;
    setSavedLikeUpdatingIds((prev) => {
      const next = new Set(prev);
      next.add(jobId);
      return next;
    });
    try {
      const url = `${API_ENDPOINTS.AUTH.JOBS}like/${jobId}/`;
      await apiClient.patch(url);
      await fetchSaved(savedPage);
    } catch {
      toast.error('Failed to update saved job. Please try again.');
    } finally {
      setSavedLikeUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  const handleShare = (job: JobCardData) => {
    if (navigator.share) {
      navigator.share({
        title: `${job.category} - ${job.eventType}`,
        text: `${job.category} under ₹${job.amount.toLocaleString()} in ${job.city}`,
        url: window.location.href,
      });
    }
  };

  const handleApply = async (job: JobCardData) => {
    const getExpressClaimBlockedMessage = (blockedReason?: string | null): string => {
      const reason = (blockedReason ?? '').trim().toLowerCase();
      if (!reason) return 'You cannot claim this express job right now.';
      switch (reason) {
        case 'same_date_conflict':
          return 'Only one express booking is allowed per day.';
        case 'already_claimed':
          return 'This booking has already been claimed by another artist.';
        case 'tier_mismatch':
          return 'This job requires a higher credibility tier.';
        case 'monthly_limit_reached':
          return 'Your monthly express booking limit has been reached.';
        default:
          return `You cannot claim this express job right now (${reason.replace(/_/g, ' ')}).`;
      }
    };
    const isFreeSubscription = (): boolean => {
      const subscriptionName = (artistDetails as { subscriptions?: { subscription_name?: string | null } } | null)
        ?.subscriptions?.subscription_name;
      const normalized = (subscriptionName ?? '').trim().toLowerCase();
      return normalized === '' || normalized === 'free';
    };

    const isExpressJob = job.badge === 'Express' || (job.type ?? '').trim().toLowerCase() === 'express';
    if (isExpressJob) {
      if (job.canClaim === false) {
        const blockedReason = (job.blockedReason ?? '').trim().toLowerCase();
        if (blockedReason === 'monthly_limit_reached' && isFreeSubscription()) {
          toast((t) => (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-white">Your monthly express booking limit has been reached.</p>
              <button
                type="button"
                onClick={() => {
                  navigate('/membership');
                  toast.dismiss(t.id);
                }}
                className="w-fit rounded-lg bg-[#F33363] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#FF5E8E]"
              >
                Upgrade plan
              </button>
            </div>
          ), { duration: 8000 });
        } else {
          toast.error(getExpressClaimBlockedMessage(job.blockedReason));
        }
        return;
      }

      const jobId = Number(job.id);
      if (!Number.isFinite(jobId)) {
        toast.error('Invalid job id.');
        return;
      }

      // For Express jobs, open BookingFillPage using deal_id from /api/v2/jobs/{id}/.
      // BookingFillPage can resolve either booking form id or deal_id.
      const tId = toast.loading('Opening booking form...');
      try {
        const res = await apiClient.get<JobDetailApiResponse>(API_ENDPOINTS.AUTH.JOBS_DETAIL(jobId));
        const dealId = res.data?.success ? res.data.data?.deal_id : undefined;
        if (!dealId) {
          toast.error('Booking info not found for this Express job.');
          return;
        }

        // Do NOT call artist/booking-forms here. Use jobs/:id payload to render the existing BookingFillPage UI.
        // This prevents the booking-forms list API from being called on open.
        const d = res.data?.data;
        const budgetStr = (d?.budget ?? '').trim();
        const feeDigits = budgetStr.replace(/[^\d]/g, '');
        const fee = feeDigits ? `₹${Number(feeDigits).toLocaleString('en-IN')}` : (budgetStr || '—');

        const syntheticForm: BookingFormItem = {
          id: Number(dealId),
          deal_id: Number(dealId),
          artist_id: 0,
          artist_type: '',
          artist_act: (artistDetails as any)?.artist_name ?? (artistDetails as any)?.user_info?.name ?? '—',
          contact_name: '',
          contact_phone: '',
          contact_email: '',
          full_address: '',
          city_state: '',
          members_count: 0,
          members_info: '[]',
          report_at: '',
          performance_duration_mins: Number(d?.duration ?? 0),
          tech_rider: '0',
          tbl_needed: 0,
          travel_boarding_lodging: '',
          event_type: String(d?.event_type ?? job.eventType ?? ''),
          venue: '',
          venue_city_state: String(d?.location ?? job.location ?? ''),
          bank_name: '',
          branch_name: '',
          ifsc_code: '',
          account_number: '',
          account_name: '',
          account_type: 'Savings',
          pan: '',
          gstin: null,
          fee: fee,
          advance_percentage: '',
          advance_amount: '',
          balance_amount: '',
          payment_terms: '',
          rating: 0,
          feedback: '',
          status: '',
          created_at: '',
          updated_at: '',
          tech_rider_value: '',
          gst_value: null,
          event_date: '',
          pin_code: null,
          venue_city: '',
          venue_state: '',
          artist_state: '',
          artist_city: '',
          google_map_link: null,
          event_start_at: null,
          taf: null,
          mode: null,
          description: String(d?.description ?? ''),
        };

        navigate(`/jobs/booking-fill/${dealId}`, { state: { form: syntheticForm } });
        return;
      } catch {
        toast.error('Failed to open booking form. Please try again.');
        return;
      } finally {
        toast.dismiss(tId);
      }
    }

    const canApply = canApplyToJob(eventPricingData, job.eventType ?? job.eventTag ?? '');
    if (!canApply) {
      toast((t) => (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-white">
            Please add your pricing for {job.eventType ?? job.eventTag} in Event Pricing to apply.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setJobForAddPricing(job);
                const match = EVENT_TYPES.find(
                  (e) =>
                    e.event_name.toLowerCase() === (job.eventType ?? job.eventTag ?? '').toLowerCase() ||
                    e.e_slug === eventTypeToSlug(job.eventType ?? job.eventTag ?? '')
                );
                setAddPricingEventSlug(match?.e_slug ?? EVENT_TYPES[0]?.e_slug ?? '');
                setAddPricingBasePrice('');
                setAddPricingSheetOpen(true);
                toast.dismiss(t.id);
              }}
              className="rounded-lg bg-[#F33363] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#FF5E8E]"
            >
              Add event pricing
            </button>

          </div>
        </div>
      ), { duration: 8000 });
      return;
    }

    const status = (artistDetails?.user_info?.profile_status ?? '').trim().toLowerCase();
    const isLiveStatus = status === 'live' || status === 'approved';
    const isIncomplete = status === 'incomplete';
    const isOnReview = status === 'on review' || status === 'on_review' || status === 'pending';

    if (isIncomplete) {
      toast.error('Please complete your profile to start applying for jobs.');
      return;
    }
    if (isOnReview) {
      toast('Your profile is currently under review. Please wait for it to go live before applying for jobs.');
      return;
    }
    if (!isLiveStatus) {
      toast.error('Your profile is not live yet. Please wait for it to go live before applying for jobs.');
      return;
    }

    setSelectedJob(job);
    setBidDrawerOpen(true);
  };

  const openAddPricingForJob = (job: JobCardData) => {
    setJobForAddPricing(job);
    const match = EVENT_TYPES.find(
      (e) =>
        e.event_name.toLowerCase() === (job.eventType ?? job.eventTag ?? '').toLowerCase() ||
        e.e_slug === eventTypeToSlug(job.eventType ?? job.eventTag ?? '')
    );
    setAddPricingEventSlug(match?.e_slug ?? EVENT_TYPES[0]?.e_slug ?? '');
    setAddPricingBasePrice('');
    setAddPricingSheetOpen(true);
  };

  const handleAddPricingSubmit = async () => {
    const slug = addPricingEventSlug?.trim();
    const price = parseFloat(addPricingBasePrice);
    if (!slug || isNaN(price) || price < 0) {
      toast.error('Please select an event type and enter a valid base price.');
      return;
    }
    setAddPricingSubmitting(true);
    try {
      await createEventPricing({
        event_slug: slug,
        base_price: price.toFixed(2),
        festival_pricing: {},
      });
      await ensureEventPricingFetched();
      setAddPricingSheetOpen(false);
      setJobForAddPricing(null);
      setAddPricingEventSlug('');
      setAddPricingBasePrice('');
      toast.success('Event pricing added. You can apply to this job now.');
    } catch {
      toast.error('Failed to add event pricing. Please try again.');
    } finally {
      setAddPricingSubmitting(false);
    }
  };

  const handleConfirmBid = () => {
    // TODO: Submit bid
    setSelectedJob(null);
  };

  const handleApplyAgainFromBid = (bid: BidListCardData) => {
    const jobForReapply: JobCardData = {
      id: bid.id,
      category: bid.eventType,
      amount: 0,
      budgetDisplay: bid.budgetDisplay,
      eventType: bid.eventType,
      city: '',
      date: bid.date,
      location: bid.location,
      durationMinutes: bid.durationMinutes,
      attendees: bid.attendees,
      eventTag: bid.eventType,
      coinsToApply: Math.max(0, Number(bid.coinsDeducted || 0)),
      isSaved: false,
      title: bid.title,
      description: bid.description || '',
      type: 'Basic',
    };
    handleApply(jobForReapply);
  };

  const REGEX_GST = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  const REGEX_IFSC = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  const REGEX_ACCOUNT = /^\d{9,18}$/;

  const openBookingForm = (form: BookingFormItem, isSubmitted: boolean) => {
    if (!isSubmitted) {
      navigate(`/jobs/booking-fill/${form.id}`, { state: { form } });
      return;
    }
    setSelectedBookingForm(form);
    setBookingIsSubmittedView(true);
    setExpressSubmittedDetails(null);
    setBookingDrawerOpen(true);
  };

  useEffect(() => {
    if (!bookingDrawerOpen) return;
    if (!bookingIsSubmittedView) return;
    if (!selectedBookingForm) return;
    if (selectedBookingForm.mode !== 'express') return;

    const dealId = Number(selectedBookingForm.deal_id || selectedBookingForm.id);
    if (!Number.isFinite(dealId)) return;

    setExpressSubmittedDetailsLoading(true);
    apiClient
      .get<{ success: boolean; data?: ExpressBookingDetailData }>(
        API_ENDPOINTS.AUTH.JOBS_EXPRESS_BOOKING_DETAIL(dealId)
      )
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          setExpressSubmittedDetails(res.data.data);
        } else {
          setExpressSubmittedDetails(null);
        }
      })
      .catch(() => setExpressSubmittedDetails(null))
      .finally(() => setExpressSubmittedDetailsLoading(false));
  }, [bookingDrawerOpen, bookingIsSubmittedView, selectedBookingForm?.id, selectedBookingForm?.deal_id, selectedBookingForm?.mode]);

  const setBookingFormMember = (index: number, field: keyof BookingFormMember, value: string | number) => {
    setBookingFormData((prev) => {
      const next = [...prev.members];
      if (next[index]) next[index] = { ...next[index], [field]: value };
      return { ...prev, members: next };
    });
  };

  const validateBookingForm = (form: BookingFormItem): string | null => {
    const count = form.members_count ?? 0;
    const members = bookingFormData.members.slice(0, count).filter((m) => String(m?.name ?? '').trim() && String(m?.role ?? '').trim());
    if (members.length < count) return 'Please fill at least ' + count + ' performing member(s) (name and role).';
    if (form.tech_rider === '1' && !bookingFormData.techRiderValue.trim()) return 'Tech rider details are required.';
    if (form.tbl_needed === 1 && !bookingFormData.travelBoardingLodging.trim()) return 'Travel / boarding / lodging details are required.';
    if (!bookingFormData.bank_name.trim()) return 'Please enter bank name.';
    if (!bookingFormData.branch_name.trim()) return 'Please enter branch name.';
    if (!REGEX_IFSC.test(bookingFormData.ifsc_code.trim())) return 'Enter a valid IFSC code.';
    if (!REGEX_ACCOUNT.test(bookingFormData.account_number.trim())) return 'Enter a valid account number (9–18 digits).';
    if (!bookingFormData.account_name.trim()) return 'Please enter account holder name.';
    if (bookingFormData.gstin.trim() && !REGEX_GST.test(bookingFormData.gstin.trim())) return 'Enter a valid GSTIN.';
    if (!bookingFormData.pan.trim()) return 'PAN number is required.';
    if (!bookingFormData.termsAccepted) return 'You must accept the terms and conditions to proceed.';
    return null;
  };

  const handleSubmitBookingForm = async () => {
    if (!selectedBookingForm || bookingIsSubmittedView) return;
    const err = validateBookingForm(selectedBookingForm);
    if (err) {
      toast.error(err);
      return;
    }
    const count = selectedBookingForm.members_count ?? 0;
    const members = bookingFormData.members
      .slice(0, count)
      .filter((m) => String(m?.name ?? '').trim() && String(m?.role ?? '').trim())
      .map((m) => ({ name: m.name.trim(), role: m.role.trim(), age: Number(m.age) || 0 }));
    const payload: BookingFormSubmitPayload = {
      members_info: JSON.stringify(members),
      tech_rider_value: selectedBookingForm.tech_rider === '1' ? bookingFormData.techRiderValue.trim() : '',
      travel_boarding_lodging: selectedBookingForm.tbl_needed === 1 ? bookingFormData.travelBoardingLodging.trim() : '',
      gstin: bookingFormData.gstin.trim(),
      pan: bookingFormData.pan.trim(),
      bank_name: bookingFormData.bank_name.trim(),
      branch_name: bookingFormData.branch_name.trim(),
      ifsc_code: bookingFormData.ifsc_code.trim(),
      account_number: bookingFormData.account_number.trim(),
      account_name: bookingFormData.account_name.trim(),
      account_type: bookingFormData.account_type.trim() || 'Savings',
      feedback: bookingFormData.feedback.trim(),
      rating: 0,
    };
    setBookingFormSubmitLoading(true);
    try {
      await apiClient.post(
        API_ENDPOINTS.AUTH.ARTIST_BOOKING_FORM_SUBMIT(selectedBookingForm.id),
        payload
      );
      toast.success('Booking form submitted successfully.');
      setBookingDrawerOpen(false);
      setSelectedBookingForm(null);
      await fetchBookingForms();
    } catch {
      toast.error('Failed to submit booking form. Please try again.');
    } finally {
      setBookingFormSubmitLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-[#0B0B0D] text-white">
      <div className="w-full md:px-4 py-10 pb-24">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Briefcase className="h-8 w-8 lg:h-10 lg:w-10 text-[#8E8E93]" />
            <h1 className="text-3xl lg:text-4xl font-bold text-white tracking-tight tracking-wide">Jobs</h1>
          </div>
          <p className="text-[#8E8E93] text-sm lg:text-base">
            Discover opportunities that match your skills
          </p>
        </div>

        <div className="space-y-6 lg:space-y-8">
          {/* Stats & Balance Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#F33363]/10 to-transparent opacity-50 transition-opacity group-hover:opacity-70" />
            <div className="relative bg-[#1C1C1E] rounded-3xl  p-6 lg:p-8 backdrop-blur-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-center gap-5">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#F33363]/20 blur-xl rounded-full" />
                    <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F33363] to-[#FF5E8E] flex items-center justify-center shadow-lg shadow-[#F33363]/20">
                      <Star className="h-7 w-7 text-white fill-white" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-xl lg:text-2xl font-bold text-white tracking-tight">Your StarCoins</h2>
                    <p className="text-[#8E8E93] text-sm mt-1">Available to apply for premium gigs</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 md:gap-8 px-6 py-4 rounded-2xl bg-[#2C2C2E]/50 border border-[#3A3A3C]/50">
                  <div className="text-center">
                    <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-1">Balance</p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl lg:text-4xl font-black text-white">
                        {artistDetails?.wallet?.balance ?? 0}
                      </span>
                      <span className="text-sm font-bold text-[#F33363]">SC</span>
                    </div>
                  </div>
                  <div className="w-px h-10 bg-[#3A3A3C]" />
                  <div className="text-center">
                    <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-1">Your Bids</p>
                    <p className="text-3xl lg:text-4xl font-black text-white">{totalAppliedJobs}</p>
                  </div>
                  <div className="w-px h-10 bg-[#3A3A3C]" />
                  <div className="text-center">
                    <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider mb-1">Active Jobs</p>
                    <p className="text-3xl lg:text-4xl font-black text-white">{jobsCount}</p>
                  </div>
                </div>


              </div>
            </div>
          </motion.div>



          {/* Tabs */}
          <div ref={tabsSectionRef} className="border-b border-[#3A3A3C]">
            <nav className="flex gap-6 lg:gap-8" aria-label="Job categories">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'relative pb-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm',
                    activeTab === tab.id
                      ? 'text-white'
                      : 'text-[#8E8E93] hover:text-white'
                  )}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <motion.span
                      layoutId="jobs-tab-underline"
                      className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#F33363] rounded-full"
                      transition={{ type: 'spring', duration: 0.3 }}
                    />
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Job / Bid cards list */}
          <div className="">
            {activeTab === 'express' && (
              <MyBookingsTabContent
                bookingFormsToFill={bookingFormsToFill}
                bookingFormsSubmitted={bookingFormsSubmitted}
                bookingsToFillLoading={bookingsToFillLoading}
                bookingsSubmittedLoading={bookingsSubmittedLoading}
                onOpenForm={openBookingForm}
                formatBookingEventDate={formatBookingEventDate}
                formatBookingINR={formatBookingINR}
                ongoingEventDealId={activeExpressEvent?.dealId ?? null}
                ongoingEventRemainingSeconds={activeExpressEvent?.remainingSeconds ?? 0}
                ongoingEventElapsedMinutes={activeExpressEvent?.elapsedMinutes ?? 0}
                spotlightBookingDealId={spotlightBookingDealId}
                onSpotlightHandled={() => setSpotlightBookingDealId(null)}
              />
            )}
            {activeTab === 'gigs' && (
              <GigsTabContent
                jobs={gigsJobs}
                loading={gigsLoading}
                page={gigsPage}
                setPage={setGigsPage}
                activeType={gigsType}
                setActiveType={setGigsType}
                gigsTypeTabs={gigsTypeTabs}
                jobsCount={jobsCount}
                onToggleLike={handleGigsToggleLike}
                likeUpdatingIds={gigsLikeUpdatingIds}
                onShare={handleShare}
                onApply={handleApply}
                onOpenAddPricingForJob={openAddPricingForJob}
                canApplyToJob={canApplyToJob}
                eventPricingData={eventPricingData}
              />
            )}
            {activeTab === 'saved' && (
              <SavedJobsTabContent
                jobs={savedJobs}
                loading={savedLoading}
                page={savedPage}
                setPage={setSavedPage}
                savedJobsCount={savedJobsCount}
                onToggleLike={handleSavedToggleLike}
                likeUpdatingIds={savedLikeUpdatingIds}
                onShare={handleShare}
                onApply={handleApply}
                onOpenAddPricingForJob={openAddPricingForJob}
                canApplyToJob={canApplyToJob}
                eventPricingData={eventPricingData}
              />
            )}
            {activeTab === 'bids' && (
              <MyBidsTabContent
                bids={bidsJobs}
                loading={bidsLoading}
                page={bidsPage}
                setPage={setBidsPage}
                bidsCount={bidsCount}
                onApplyAgain={handleApplyAgainFromBid}
              />
            )}
          </div>
        </div>
      </div>

      <ApplyBidDrawer
        open={bidDrawerOpen}
        onOpenChange={(open) => {
          setBidDrawerOpen(open);
          if (!open) setSelectedJob(null);
        }}
        job={selectedJob}
        onConfirm={handleConfirmBid}
        onBidSuccess={() => {
          void fetchBids(1);
          void fetchGigs(gigsPage, gigsType);
        }}
        eventPricingBasePrice={
          selectedJob
            ? (() => {
              const item = getEventPricingForJob(eventPricingData, selectedJob.eventType ?? selectedJob.eventTag ?? '');
              return item && item.base_price ? parseFloat(item.base_price) : null;
            })()
            : null
        }
        clientBudgetDisplay={selectedJob?.budgetDisplay ?? (selectedJob ? `₹${selectedJob.amount.toLocaleString()}` : undefined)}
      />

      {/* Add Event Pricing sheet — add pricing for job's event type without leaving Jobs page */}
      <Sheet
        open={addPricingSheetOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAddPricingSheetOpen(false);
            setJobForAddPricing(null);
            setAddPricingEventSlug('');
            setAddPricingBasePrice('');
          }
        }}
      >
        {jobForAddPricing && (
          <div className="space-y-5 pt-2 w-full">
            <div className="flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-[#F33363]" />
              <h2 className="text-xl font-bold text-white">Add event pricing</h2>
            </div>
            <p className="text-sm text-[#8E8E93]">
              Add your pricing for <span className="font-semibold text-white">{jobForAddPricing.eventType ?? jobForAddPricing.eventTag}</span> so you can apply to this job.
            </p>
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-[#8E8E93]">Event type</label>
              <select
                value={addPricingEventSlug}
                onChange={(e) => setAddPricingEventSlug(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-[#3A3A3C] bg-[#1C1C1E] text-white text-sm [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-[#F33363]"
              >
                {EVENT_TYPES.map((e) => (
                  <option
                    key={e.e_slug}
                    value={e.e_slug}
                    className="bg-[#1C1C1E] text-white"
                  >
                    {toEventTypeDisplayLabel(e.event_name)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-[#8E8E93]">Base price (₹)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/4 -translate-y-1/2 text-[#8E8E93] text-lg">₹</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={addPricingBasePrice}
                  onChange={(e) => setAddPricingBasePrice(e.target.value.replace(/\D/g, ''))}
                  placeholder="0"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#3A3A3C] bg-transparent text-white placeholder-[#8E8E93] focus:outline-none focus:ring-2 focus:ring-[#F33363]"
                />
              </div>
            </div>
            <Button
              variant="active"
              onClick={() => void handleAddPricingSubmit()}
              disabled={addPricingSubmitting || !addPricingBasePrice.trim()}
              isLoading={addPricingSubmitting}
              className="w-full py-3.5 font-medium"
            >
              Save & apply to job
            </Button>
            <button
              type="button"
              onClick={() => navigate('/profile/event-essentials')}
              className="w-full text-center text-sm text-[#8E8E93] hover:underline"
            >
              Or manage all pricing in Event Essentials →
            </button>
          </div>
        )}
      </Sheet>

      {/* My Bookings: fill form or read-only detail */}
      <Sheet
        open={bookingDrawerOpen}
        onOpenChange={(open) => {
          setBookingDrawerOpen(open);
          if (!open) setSelectedBookingForm(null);
        }}
      >
        {selectedBookingForm && (
          <div className="space-y-6 pt-2 w-full max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h2 className="text-xl lg:text-2xl font-bold text-white">{selectedBookingForm.artist_act} · {selectedBookingForm.event_type}</h2>
                <p className="text-[#8E8E93] text-sm">Booking #{selectedBookingForm.id}</p>
              </div>
              {bookingIsSubmittedView && (
                <span className="px-3 py-1 rounded-lg bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-xs font-bold uppercase tracking-wider flex-shrink-0">Submitted</span>
              )}
            </div>

            {bookingIsSubmittedView && selectedBookingForm.mode === 'express' ? (
              <>
                {/* Express-only submitted view */}
                {expressSubmittedDetailsLoading ? (
                  <div className="rounded-2xl p-8 text-center flex items-center justify-center">
                    <Loader size="small" />
                  </div>
                ) : (
                  <>
                    {/* Read-only: Contact */}
                    <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
                      <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Contact information</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div><span className="text-[#8E8E93]">Contact name</span><p className="text-white font-medium">{expressSubmittedDetails?.artist_details?.professional_name ?? selectedBookingForm.contact_name}</p></div>
                        <div><span className="text-[#8E8E93]">Phone</span><p className="text-white font-medium">{expressSubmittedDetails?.artist_details?.phone ?? selectedBookingForm.contact_phone}</p></div>
                        <div className="sm:col-span-2"><span className="text-[#8E8E93]">Email</span><p className="text-white font-medium">{expressSubmittedDetails?.artist_details?.email ?? selectedBookingForm.contact_email}</p></div>
                        <div className="sm:col-span-2"><span className="text-[#8E8E93]">Address</span><p className="text-white font-medium">{(expressSubmittedDetails?.location?.address ?? selectedBookingForm.full_address) || '—'}</p></div>
                      </div>
                    </div>

                    {/* Read-only: Event */}
                    <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
                      <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Event information</p>
                      {(() => {
                        const startTime = (expressSubmittedDetails?.event_details?.event_time ?? '').trim();
                        const reportRaw = subtractMinutes(startTime, 15);
                        const duration = expressSubmittedDetails?.event_details?.event_duration;
                        const city = (expressSubmittedDetails?.location?.city ?? '').trim();
                        const state = (expressSubmittedDetails?.location?.state ?? '').trim();
                        const cityState = [city, state].filter(Boolean).join(', ').trim();
                        const venue = (expressSubmittedDetails?.location?.address ?? '').trim() || selectedBookingForm.venue;
                        return (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div><span className="text-[#8E8E93]">Artist act</span><p className="text-white font-medium">{selectedBookingForm.artist_act}</p></div>
                            <div><span className="text-[#8E8E93]">Event type</span><p className="text-white font-medium">{expressSubmittedDetails?.event_details?.event_type ?? selectedBookingForm.event_type}</p></div>
                            <div><span className="text-[#8E8E93]">Event date</span><p className="text-white font-medium">{formatBookingEventDateLong(expressSubmittedDetails?.event_details?.event_date ?? selectedBookingForm.event_date)}</p></div>
                            <div><span className="text-[#8E8E93]">Start time</span><p className="text-white font-medium">{toAmPm(startTime || selectedBookingForm.event_start_at)}</p></div>
                            <div><span className="text-[#8E8E93]">Report time</span><p className="text-white font-medium">{toAmPm(reportRaw ?? selectedBookingForm.report_at)}</p></div>
                            <div><span className="text-[#8E8E93]">Duration</span><p className="text-white font-medium">{Number(duration ?? selectedBookingForm.performance_duration_mins ?? 0) || 0} mins</p></div>
                            <div className="sm:col-span-2"><span className="text-[#8E8E93]">Venue</span><p className="text-white font-medium">{venue || '—'}</p></div>
                            {cityState && <div><span className="text-[#8E8E93]">City / State</span><p className="text-white font-medium">{cityState}</p></div>}
                            {expressSubmittedDetails?.location?.map_link && (
                              <div className="sm:col-span-2">
                                <a href={expressSubmittedDetails.location.map_link} target="_blank" rel="noopener noreferrer" className="text-[#F33363] text-sm hover:underline inline-flex items-center gap-1.5">
                                  View on Map <MapPin className="h-3.5 w-3.5" />
                                </a>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Performing members (Express) */}
                    <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
                      <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Performing members</p>
                      <div className="flex items-center justify-between">
                        <p className="text-white font-medium">{(expressSubmittedDetails?.performing_members?.label ?? '').trim() || '—'}</p>
                        <p className="text-white font-bold">{expressSubmittedDetails?.performing_members?.count ?? '—'}</p>
                      </div>
                    </div>

                    {/* Additional information (Express) */}
                    <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
                      <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Additional information</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-[#8E8E93]">Members count</span><p className="text-white font-medium">{expressSubmittedDetails?.performing_members?.count ?? '—'}</p></div>
                        <div><span className="text-[#8E8E93]">Status</span><p className="text-white font-medium capitalize">{selectedBookingForm.status || '—'}</p></div>
                        <div className="sm:col-span-2">
                          <span className="text-[#8E8E93]">Tech rider</span>
                          <p className="text-white font-medium">
                            {expressSubmittedDetails?.fee_and_payment?.is_sound_setup_added === true
                              ? 'Sound setup included in your fee. Artist must carry 1 mic with stand, 1 mixer, 2 speakers and 2 AUX cables.'
                              : 'Not required for this gathering size.'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Tech rider & travel details (Express) */}
                    <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
                      <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Tech rider & travel details</p>
                      <p className="text-white text-sm">Included in your fee</p>
                    </div>

                    {/* Fee & Payment (Express) */}
                    <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
                      <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Fee & Payment</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div>
                          <span className="text-[#8E8E93]">
                            Fee{expressSubmittedDetails?.fee_and_payment?.is_sound_setup_added === true ? ' (incl. Sound Setup)' : ''}
                          </span>
                          <p className="text-white font-medium">{formatBookingINR(expressSubmittedDetails?.fee_and_payment?.base_price)}</p>
                        </div>
                        <div><span className="text-[#8E8E93]">TAF</span><p className="text-white font-medium">{expressSubmittedDetails?.fee_and_payment?.taf_percent ?? '—'}%</p></div>
                        <div><span className="text-[#8E8E93]">Balance</span><p className="text-white font-medium">{formatBookingINR(expressSubmittedDetails?.fee_and_payment?.final_balance)}</p></div>
                        <div className="sm:col-span-2"><span className="text-[#8E8E93]">Payment terms</span><p className="text-white font-medium">{formatPaymentTerms(expressSubmittedDetails?.fee_and_payment?.payment_terms ?? selectedBookingForm.payment_terms)}</p></div>
                      </div>
                    </div>

                    {/* PAN DETAILS (Express) */}
                    <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
                      <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">PAN DETAILS</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div><span className="text-[#8E8E93]">PAN number</span><p className="text-white font-medium">{selectedBookingForm.pan || '—'}</p></div>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : null}

            {!(bookingIsSubmittedView && selectedBookingForm.mode === 'express') && (
              <>
                {/* Read-only: Contact */}
                <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
                  <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Contact information</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-[#8E8E93]">Contact name</span><p className="text-white font-medium">{selectedBookingForm.contact_name}</p></div>
                    <div><span className="text-[#8E8E93]">Phone</span><p className="text-white font-medium">{selectedBookingForm.contact_phone}</p></div>
                    <div className="sm:col-span-2"><span className="text-[#8E8E93]">Email</span><p className="text-white font-medium">{selectedBookingForm.contact_email}</p></div>
                    <div className="sm:col-span-2"><span className="text-[#8E8E93]">Address</span><p className="text-white font-medium">{selectedBookingForm.full_address || '—'}</p></div>
                  </div>
                </div>

            {/* Read-only: Event */}
            <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
              <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Event information</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-[#8E8E93]">Artist act</span><p className="text-white font-medium">{selectedBookingForm.artist_act}</p></div>
                <div><span className="text-[#8E8E93]">Event type</span><p className="text-white font-medium">{selectedBookingForm.event_type}</p></div>
                <div><span className="text-[#8E8E93]">Event date</span><p className="text-white font-medium">{bookingIsSubmittedView ? formatBookingEventDateLong(selectedBookingForm.event_date) : formatBookingEventDate(selectedBookingForm.event_date)}</p></div>
                {selectedBookingForm.event_start_at && <div><span className="text-[#8E8E93]">Event start time</span><p className="text-white font-medium">{removeSecondsFromTime(selectedBookingForm.event_start_at)}</p></div>}
                <div><span className="text-[#8E8E93]">Report time</span><p className="text-white font-medium">{removeSecondsFromTime(selectedBookingForm.report_at)}</p></div>
                <div><span className="text-[#8E8E93]">Duration</span><p className="text-white font-medium">{selectedBookingForm.performance_duration_mins} mins</p></div>
                <div className="sm:col-span-2"><span className="text-[#8E8E93]">Venue</span><p className="text-white font-medium">{selectedBookingForm.venue}</p></div>
                {selectedBookingForm.venue_city_state && <div><span className="text-[#8E8E93]">City / State</span><p className="text-white font-medium">{selectedBookingForm.venue_city_state}</p></div>}
                <div className="sm:col-span-2"><span className="text-[#8E8E93]">Address</span><p className="text-white font-medium">{selectedBookingForm.full_address || '—'}</p></div>
                {selectedBookingForm.description && <div className="sm:col-span-2"><span className="text-[#8E8E93]">Description</span><p className="text-white font-medium whitespace-pre-wrap">{selectedBookingForm.description}</p></div>}
                {selectedBookingForm.google_map_link && <div className="sm:col-span-2"><a href={selectedBookingForm.google_map_link} target="_blank" rel="noopener noreferrer" className="text-[#F33363] text-sm hover:underline inline-flex items-center gap-1.5">View on Map <MapPin className="h-3.5 w-3.5" /></a></div>}
              </div>
            </div>

            {/* Performing members: editable when filling */}
            <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
              <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Performing members ({selectedBookingForm.members_count} required)</p>
              {bookingIsSubmittedView ? (
                <ul className="space-y-2 text-sm">
                  {parseMembersInfo(selectedBookingForm.members_info).map((m, i) => (
                    <li key={i} className="text-white">{m.name} · {m.role}{m.age ? ` · ${m.age}` : ''}</li>
                  ))}
                </ul>
              ) : (
                <div className="space-y-3">
                  {bookingFormData.members.slice(0, selectedBookingForm.members_count).map((m, i) => (
                    <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <input placeholder="Name" value={m.name} onChange={(e) => setBookingFormMember(i, 'name', e.target.value)} className="px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm focus:ring-2 focus:ring-[#F33363]" />
                      <input placeholder="Role" value={m.role} onChange={(e) => setBookingFormMember(i, 'role', e.target.value)} className="px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm focus:ring-2 focus:ring-[#F33363]" />
                      <input type="number" placeholder="Age" value={m.age || ''} onChange={(e) => setBookingFormMember(i, 'age', e.target.value ? parseInt(e.target.value, 10) : 0)} className="px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm focus:ring-2 focus:ring-[#F33363]" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Additional Information (submitted view only) */}
            {bookingIsSubmittedView && (
              <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
                <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Additional information</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-[#8E8E93]">Artist type</span><p className="text-white font-medium">{selectedBookingForm.artist_type || '—'}</p></div>
                  <div><span className="text-[#8E8E93]">Members count</span><p className="text-white font-medium">{selectedBookingForm.members_count}</p></div>
                  <div><span className="text-[#8E8E93]">Status</span><p className="text-white font-medium capitalize">{selectedBookingForm.status || '—'}</p></div>
                  <div><span className="text-[#8E8E93]">Tech rider</span><p className="text-white font-medium">{selectedBookingForm.tech_rider === '1' ? 'Yes' : 'No'}</p></div>
                </div>
              </div>
            )}

            {/* Tech rider & TBL */}
            <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
              <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Tech rider & travel details</p>
              {selectedBookingForm.tech_rider === '1' ? (
                bookingIsSubmittedView ? (
                  <div className="space-y-3">
                    <div><p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider mb-1">Tech rider details</p><p className="text-white text-sm whitespace-pre-wrap">{selectedBookingForm.tech_rider_value || '—'}</p></div>
                    {selectedBookingForm.tbl_needed === 1 && <div><p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider mb-1">Travel, boarding & lodging</p><p className="text-white text-sm whitespace-pre-wrap">{selectedBookingForm.travel_boarding_lodging || '—'}</p></div>}
                  </div>
                ) : (
                  <>
                    <textarea placeholder="Tech rider details" value={bookingFormData.techRiderValue} onChange={(e) => setBookingFormData((p) => ({ ...p, techRiderValue: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm min-h-[80px] focus:ring-2 focus:ring-[#F33363]" rows={3} />
                    {selectedBookingForm.tbl_needed === 1 && <textarea placeholder="Travel / boarding / lodging requirements" value={bookingFormData.travelBoardingLodging} onChange={(e) => setBookingFormData((p) => ({ ...p, travelBoardingLodging: e.target.value }))} className="w-full mt-2 px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm min-h-[80px] focus:ring-2 focus:ring-[#F33363]" rows={3} />}
                  </>
                )
              ) : (
                bookingIsSubmittedView ? (
                  selectedBookingForm.tbl_needed === 1 ? (
                    <div><p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider mb-1">Travel, boarding & lodging</p><p className="text-white text-sm whitespace-pre-wrap">{selectedBookingForm.travel_boarding_lodging || '—'}</p></div>
                  ) : (
                    <p className="text-[#8E8E93] text-sm">Not applicable</p>
                  )
                ) : (
                  <>
                    <p className="text-[#8E8E93] text-sm">Tech rider: Not applicable</p>
                    {selectedBookingForm.tbl_needed === 1 && <textarea placeholder="Travel / boarding / lodging requirements" value={bookingFormData.travelBoardingLodging} onChange={(e) => setBookingFormData((p) => ({ ...p, travelBoardingLodging: e.target.value }))} className="w-full mt-2 px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm min-h-[80px] focus:ring-2 focus:ring-[#F33363]" rows={3} />}
                  </>
                )
              )}
            </div>

            {/* Payment (read-only) */}
            <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
              <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Financial information</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><span className="text-[#8E8E93]">Total fee</span><p className="text-white font-medium">{formatBookingINR(selectedBookingForm.fee)}</p></div>
                <div><span className="text-[#8E8E93]">Advance percentage</span><p className="text-white font-medium">{selectedBookingForm.advance_percentage}%</p></div>
                <div><span className="text-[#8E8E93]">Advance amount</span><p className="text-white font-medium">{formatBookingINR(selectedBookingForm.advance_amount)}</p></div>
                <div><span className="text-[#8E8E93]">Balance amount</span><p className="text-white font-medium">{formatBookingINR(selectedBookingForm.balance_amount)}</p></div>
                <div className="sm:col-span-2"><span className="text-[#8E8E93]">Payment terms</span><p className="text-white font-medium">{formatPaymentTerms(selectedBookingForm.payment_terms)}</p></div>
              </div>
              {selectedBookingForm.mode === 'auto' && selectedBookingForm.taf && <p className="text-[#8E8E93] text-xs">TAF {selectedBookingForm.taf}% included</p>}
            </div>

            {/* Bank details: editable when filling */}
            <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
              <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">Bank details</p>
              {bookingIsSubmittedView ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-[#8E8E93]">Bank name</span><p className="text-white font-medium">{selectedBookingForm.bank_name}</p></div>
                  <div><span className="text-[#8E8E93]">Branch</span><p className="text-white font-medium">{selectedBookingForm.branch_name}</p></div>
                  <div><span className="text-[#8E8E93]">IFSC code</span><p className="text-white font-medium">{selectedBookingForm.ifsc_code}</p></div>
                  <div><span className="text-[#8E8E93]">Account number</span><p className="text-white font-medium">{selectedBookingForm.account_number}</p></div>
                  <div><span className="text-[#8E8E93]">Account holder</span><p className="text-white font-medium">{selectedBookingForm.account_name}</p></div>
                  <div><span className="text-[#8E8E93]">Account type</span><p className="text-white font-medium">{selectedBookingForm.account_type}</p></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input placeholder="Bank name" value={bookingFormData.bank_name} onChange={(e) => setBookingFormData((p) => ({ ...p, bank_name: e.target.value }))} className="px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm focus:ring-2 focus:ring-[#F33363]" />
                  <input placeholder="Branch" value={bookingFormData.branch_name} onChange={(e) => setBookingFormData((p) => ({ ...p, branch_name: e.target.value }))} className="px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm focus:ring-2 focus:ring-[#F33363]" />
                  <input placeholder="IFSC" value={bookingFormData.ifsc_code} onChange={(e) => setBookingFormData((p) => ({ ...p, ifsc_code: e.target.value.toUpperCase() }))} className="px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm focus:ring-2 focus:ring-[#F33363]" />
                  <input placeholder="Account number" value={bookingFormData.account_number} onChange={(e) => setBookingFormData((p) => ({ ...p, account_number: e.target.value }))} className="px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm focus:ring-2 focus:ring-[#F33363]" />
                  <input placeholder="Account holder" value={bookingFormData.account_name} onChange={(e) => setBookingFormData((p) => ({ ...p, account_name: e.target.value }))} className="px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm focus:ring-2 focus:ring-[#F33363]" />
                  <select value={bookingFormData.account_type} onChange={(e) => setBookingFormData((p) => ({ ...p, account_type: e.target.value }))} className="px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm focus:ring-2 focus:ring-[#F33363]">
                    <option value="Savings">Savings</option>
                    <option value="Current">Current</option>
                  </select>
                </div>
              )}
            </div>

            {/* GST & PAN */}
            <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C] space-y-3">
              <p className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-wider">GST & PAN details</p>
              {bookingIsSubmittedView ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-[#8E8E93]">PAN number</span><p className="text-white font-medium">{selectedBookingForm.pan || '—'}</p></div>
                  <div><span className="text-[#8E8E93]">GSTIN number</span><p className="text-white font-medium">{selectedBookingForm.gstin?.trim() ? selectedBookingForm.gstin : 'Not provided'}</p></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input placeholder="PAN" value={bookingFormData.pan} onChange={(e) => setBookingFormData((p) => ({ ...p, pan: e.target.value.toUpperCase() }))} className="px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm focus:ring-2 focus:ring-[#F33363]" maxLength={10} />
                  <input placeholder="GSTIN (optional)" value={bookingFormData.gstin} onChange={(e) => setBookingFormData((p) => ({ ...p, gstin: e.target.value.toUpperCase() }))} className="px-3 py-2 rounded-lg border border-[#3A3A3C] bg-transparent text-white text-sm focus:ring-2 focus:ring-[#F33363]" />
                </div>
              )}
            </div>

            {/* Terms & submit (only when filling) */}
            {!bookingIsSubmittedView && (
              <>
                <div className="p-4 rounded-xl bg-[#1C1C1E] border border-[#3A3A3C]">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={bookingFormData.termsAccepted} onChange={(e) => setBookingFormData((p) => ({ ...p, termsAccepted: e.target.checked }))} className="mt-1 rounded border-[#3A3A3C] bg-transparent text-[#F33363] focus:ring-[#F33363]" />
                    <span className="text-sm text-[#8E8E93]">I accept the Booking Terms and Conditions (Annexure A).</span>
                  </label>
                </div>
                <Button
                  type="button"
                  onClick={handleSubmitBookingForm}
                  disabled={bookingFormSubmitLoading}
                  isLoading={bookingFormSubmitLoading}
                  variant="active"
                  className="w-full py-4 rounded-xl font-bold bg-[#F33363] hover:bg-[#FF5E8E] text-white shadow-lg shadow-[#F33363]/20"
                >
                  Submit form
                </Button>
              </>
            )}
              </>
            )}
          </div>
        )}
      </Sheet>
    </div>
  );
};
